/**
 * AuditLogList — TASK-022 / flow-04
 * Actor: sys_admin, biz_admin
 * Route: /admin/audit-logs
 * API: GET /api/v1/audit-logs (paginated)
 */
import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AppButton, AppCard, AppField, AppInput } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { api } from "../../../shared/api/client";

type AuditLog = {
  id: number;
  action: string;
  resource_type: string;
  resource_id: string | null;
  username: string;
  ip_address: string | null;
  created_at: string;
};

export function AuditLogList() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [pageState, setPageState] = useState<"loading"|"error"|"success">("loading");
  const [pageError, setPageError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 50;

  const load = async () => {
    setPageState("loading");
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE), ...(search ? { action: search } : {}) });
      const d = await api.get<{ logs: AuditLog[]; total: number }>(`/audit-logs?${params}`);
      setLogs(d.logs ?? []);
      setTotal(d.total ?? 0);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load audit logs");
      setPageState("error");
    }
  };

  useEffect(() => { load(); }, [page]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); }, [search]);

  const columns: ColumnDef<AuditLog, unknown>[] = [
    { id: "created_at", header: "When", accessorFn: (r) => new Date(r.created_at).toLocaleString("en-GB") },
    { accessorKey: "action", header: "Action" },
    { accessorKey: "resource_type", header: "Resource" },
    { accessorKey: "resource_id", header: "Resource ID" },
    { accessorKey: "username", header: "By" },
    { accessorKey: "ip_address", header: "IP" },
  ];

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <>
      <PageHeader
        title="Audit Logs"
        subtitle={`${total} entr${total === 1 ? "y" : "ies"}`}
      />

      <div className="pg-list-toolbar">
        <AppField label="">
          <AppInput id="auditlog-search" type="search" placeholder="Filter by action…"
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 300 }} />
        </AppField>
      </div>

      <PageState status={pageState} error={pageError} onRetry={load}>
        <AppCard>
          <AppDataTable data={logs} columns={columns} />
          {totalPages > 1 && (
            <div className="pg-pagination">
              <AppButton type="button" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</AppButton>
              <span>Page {page} of {totalPages}</span>
              <AppButton type="button" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</AppButton>
            </div>
          )}
        </AppCard>
      </PageState>
    </>
  );
}
